import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollText, ChevronLeft, ChevronRight } from "lucide-react";
import type { ActivityLog } from "@shared/schema";

const PAGE_SIZE = 50;

const categoryLabels: Record<string, string> = {
  auth: "Inloggning",
  order: "Ordrar",
  production: "Produktion",
  client: "Kunder",
};

const actionLabels: Record<string, { label: string; className: string }> = {
  admin_login: { label: "Admin inloggning", className: "bg-blue-500/10 text-blue-600 border-blue-500/20" },
  client_login: { label: "Kund inloggning", className: "bg-cyan-500/10 text-cyan-600 border-cyan-500/20" },
  order_created: { label: "Order skapad", className: "bg-green-500/10 text-green-600 border-green-500/20" },
  order_updated: { label: "Order uppdaterad", className: "bg-amber-500/10 text-amber-600 border-amber-500/20" },
  order_edited: { label: "Order redigerad", className: "bg-amber-500/10 text-amber-600 border-amber-500/20" },
  order_deleted: { label: "Order raderad", className: "bg-red-500/10 text-red-600 border-red-500/20" },
  production_started: { label: "Produktion startad", className: "bg-purple-500/10 text-purple-600 border-purple-500/20" },
  production_cancelled: { label: "Produktion avbruten", className: "bg-orange-500/10 text-orange-600 border-orange-500/20" },
  production_progress: { label: "Produktionssteg", className: "bg-indigo-500/10 text-indigo-600 border-indigo-500/20" },
  production_finished: { label: "Produktion klar", className: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" },
  credentials_updated: { label: "Inloggningsuppgifter", className: "bg-teal-500/10 text-teal-600 border-teal-500/20" },
};

export default function AdminActivityLog() {
  const [category, setCategory] = useState("all");
  const [page, setPage] = useState(0);

  const queryParams = new URLSearchParams();
  queryParams.set("limit", String(PAGE_SIZE));
  queryParams.set("offset", String(page * PAGE_SIZE));
  if (category !== "all") queryParams.set("category", category);
  const queryUrl = `/api/admin/activity-logs?${queryParams.toString()}`;

  const { data, isLoading } = useQuery<{ logs: ActivityLog[]; total: number }>({
    queryKey: [queryUrl],
  });

  const logs = data?.logs || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const getActionBadge = (action: string) => {
    const info = actionLabels[action] || { label: action, className: "" };
    return <Badge variant="outline" className={info.className}>{info.label}</Badge>;
  };

  const formatDate = (dateStr: string | Date | null) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleString("sv-SE", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  return (
    <div className="space-y-6" data-testid="page-activity-log">
      <div>
        <h1 className="text-2xl font-bold" data-testid="text-activity-log-title">Systemlogg</h1>
        <p className="text-muted-foreground">Alla händelser i systemet</p>
      </div>

      <Card>
        <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <div>
            <CardTitle>Händelselogg</CardTitle>
            <CardDescription data-testid="text-log-total">{total} händelser totalt</CardDescription>
          </div>
          <Select value={category} onValueChange={(val) => { setCategory(val); setPage(0); }}>
            <SelectTrigger className="w-[180px]" data-testid="select-log-category">
              <SelectValue placeholder="Alla kategorier" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alla kategorier</SelectItem>
              <SelectItem value="auth">Inloggning</SelectItem>
              <SelectItem value="order">Ordrar</SelectItem>
              <SelectItem value="production">Produktion</SelectItem>
              <SelectItem value="client">Kunder</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : logs.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tid</TableHead>
                      <TableHead>Händelse</TableHead>
                      <TableHead>Kategori</TableHead>
                      <TableHead>Beskrivning</TableHead>
                      <TableHead>Användare</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow key={log.id} data-testid={`row-log-${log.id}`}>
                        <TableCell className="text-sm whitespace-nowrap text-muted-foreground">
                          {formatDate(log.createdAt)}
                        </TableCell>
                        <TableCell>{getActionBadge(log.action)}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {categoryLabels[log.category] || log.category}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm max-w-md">{log.description}</TableCell>
                        <TableCell className="text-sm font-medium">{log.actor || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-between gap-2 pt-4">
                  <p className="text-sm text-muted-foreground">
                    Sida {page + 1} av {totalPages}
                  </p>
                  <div className="flex items-center gap-1">
                    <Button
                      size="icon"
                      variant="outline"
                      disabled={page === 0}
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      data-testid="button-log-prev"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="outline"
                      disabled={page >= totalPages - 1}
                      onClick={() => setPage((p) => p + 1)}
                      data-testid="button-log-next"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="py-12 text-center" data-testid="log-empty-state">
              <ScrollText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Inga loggposter att visa</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
